import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft, 
  FileText, 
  Calendar, 
  User, 
  Clock,
  Download,
  Edit,
  AlertTriangle,
  Loader2,
  ExternalLink,
  Home,
  Maximize2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to safely render content that might be an object
const renderContent = (content) => {
  if (content === null || content === undefined) {
    return 'No content available';
  }
  if (typeof content === 'string') {
    return content;
  }
  if (typeof content === 'object') {
    // Handle objects by converting to formatted string
    try {
      return JSON.stringify(content, null, 2);
    } catch {
      return 'Content could not be displayed';
    }
  }
  return String(content);
};

export default function ReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [report, setReport] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [contentExpanded, setContentExpanded] = useState(true);
  const [showFullContent, setShowFullContent] = useState(false);

  useEffect(() => {
    if (id) {
      fetchReport();
    }
  }, [id]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch report
      const reportResponse = await axios.get(`${API}/reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(reportResponse.data);
      
      // If report has inspection_id, fetch the inspection details
      if (reportResponse.data.inspection_id) {
        try {
          const inspectionResponse = await axios.get(
            `${API}/inspections/${reportResponse.data.inspection_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setInspection(inspectionResponse.data);
        } catch (inspErr) {
          console.warn('Could not fetch linked inspection:', inspErr);
        }
      }
    } catch (err) {
      console.error('Failed to load report:', err);
      if (err.response?.status === 404) {
        setError('Report not found');
      } else {
        setError('Failed to load report details');
      }
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-secondary text-secondary-foreground';
      case 'in_progress':
        return 'bg-warning text-white';
      case 'completed':
        return 'bg-success text-white';
      case 'approved':
        return 'bg-primary text-white';
      default:
        return 'bg-secondary';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleGenerateReport = async (format = 'pdf') => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/reports/${id}/generate`,
        { format },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'docx' 
          ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
          : 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${id}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Report generated as ${format.toUpperCase()}`);
      
      // Refresh report data to get updated content
      fetchReport();
    } catch (err) {
      console.error('Generate error:', err);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="report-detail-loading">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading report...</span>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="space-y-6" data-testid="report-detail-error">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/reports')}
            className="mb-4"
            data-testid="back-to-reports"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports
          </Button>
          
          <Card className="border-destructive">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <h2 className="text-xl font-semibold text-destructive mb-2">{error}</h2>
              <p className="text-muted-foreground mb-4">
                The report you're looking for could not be loaded.
              </p>
              <Button onClick={() => navigate('/reports')}>
                Return to Reports
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (!report) {
    return null;
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="report-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/reports')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-accent" />
                <h1 className="text-2xl font-bold text-primary" data-testid="report-title">
                  {report.title || 'Untitled Report'}
                </h1>
                <Badge className={getStatusColor(report.status)} data-testid="report-status">
                  {report.status?.replace('_', ' ') || 'Unknown'}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1" data-testid="report-id">
                ID: {report.id}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              data-testid="edit-report-btn"
              onClick={() => navigate(`/reports/${id}/edit`)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button 
              variant="outline" 
              data-testid="download-report-btn"
              onClick={() => handleGenerateReport('pdf')}
              disabled={generating}
            >
              {generating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download PDF
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Linked Inspection Card */}
            {report.inspection_id && (
              <Card data-testid="linked-inspection-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="h-5 w-5 text-primary" />
                    Linked Inspection
                  </CardTitle>
                  <CardDescription>
                    This report is associated with an inspection
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {inspection ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium" data-testid="inspection-address">
                            {inspection.property_address}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {inspection.valuation_amount && (
                              <span>Valuation: £{inspection.valuation_amount.toLocaleString()}</span>
                            )}
                          </p>
                        </div>
                        <Badge className={
                          inspection.status === 'active' ? 'bg-accent text-white' :
                          inspection.status === 'in_progress' ? 'bg-warning text-white' :
                          'bg-success text-white'
                        }>
                          {inspection.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={() => navigate(`/inspections/${report.inspection_id}`)}
                        data-testid="open-inspection-btn"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open Inspection
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        Inspection ID: {report.inspection_id}
                      </p>
                      <Button 
                        variant="outline" 
                        onClick={() => navigate(`/inspections/${report.inspection_id}`)}
                        data-testid="open-inspection-btn"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Inspection
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Report Content Card */}
            <Card data-testid="report-content-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Report Content
                  </CardTitle>
                  <CardDescription>
                    Generated report sections and data
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  {report.content && Object.keys(report.content).length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowFullContent(true)}
                        data-testid="open-fullscreen-btn"
                      >
                        <Maximize2 className="h-4 w-4 mr-1" />
                        Open
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setContentExpanded(!contentExpanded)}
                        data-testid="toggle-content-btn"
                      >
                        {contentExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </CardHeader>
              {contentExpanded && (
                <CardContent>
                  {report.content && Object.keys(report.content).length > 0 ? (
                    <div className="space-y-6" data-testid="report-content">
                      {Object.entries(report.content)
                        .sort((a, b) => (a[1]?.order || 0) - (b[1]?.order || 0))
                        .map(([key, section]) => (
                          <div key={key} className="border-b pb-4 last:border-b-0">
                            <h3 className="font-semibold text-lg mb-2">
                              {section?.title || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </h3>
                            <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                              {renderContent(section?.content)}
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No content generated yet</p>
                      <p className="text-sm mt-1">Generate the report to view content here</p>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          </div>

          {/* Right Column - Metadata */}
          <div className="space-y-6">
            {/* Timeline Card */}
            <Card data-testid="timeline-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-success mt-2" />
                  <div>
                    <p className="text-sm font-medium">Created</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(report.created_at)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                  <div>
                    <p className="text-sm font-medium">Last Updated</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(report.updated_at)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Creator Card */}
            <Card data-testid="creator-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Created By
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.created_by && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">User ID</p>
                    <p className="font-mono text-sm truncate">{report.created_by}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions Card */}
            <Card data-testid="quick-actions-card">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  className="w-full" 
                  variant="outline" 
                  data-testid="regenerate-btn"
                  onClick={() => handleGenerateReport('pdf')}
                  disabled={generating}
                >
                  {generating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  {generating ? 'Generating...' : 'Regenerate Report (PDF)'}
                </Button>
                <Button 
                  className="w-full" 
                  variant="outline" 
                  data-testid="export-word-btn"
                  onClick={() => handleGenerateReport('docx')}
                  disabled={generating}
                >
                  {generating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export to Word
                </Button>
                {report.inspection_id && (
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => navigate(`/inspections/${report.inspection_id}`)}
                    data-testid="view-inspection-action-btn"
                  >
                    <Home className="h-4 w-4 mr-2" />
                    View Inspection
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Full Screen Content Dialog */}
      <Dialog open={showFullContent} onOpenChange={setShowFullContent}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Report Content - {report?.title || 'Report'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {report?.content && Object.entries(report.content)
              .sort((a, b) => (a[1]?.order || 0) - (b[1]?.order || 0))
              .map(([key, section]) => (
                <div key={key} className="border-b pb-4 last:border-b-0">
                  <h3 className="font-semibold text-lg mb-2 text-primary">
                    {section?.title || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </h3>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">
                    {renderContent(section?.content)}
                  </div>
                </div>
              ))
            }
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowFullContent(false)}>
              Close
            </Button>
            <Button onClick={() => handleGenerateReport('pdf')} disabled={generating}>
              {generating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
